"use client"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { cn } from "@/lib/utils"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  ArrowLeft,
  Building2,
  Users,
  Briefcase,
  MapPin,
  Globe,
  Mail,
  Phone,
  Calendar,
  Plus,
  Send,
  Pencil,
  Save,
  X,
} from "lucide-react"
import { EmailDialog } from "@/components/email-dialog"
import { useCallback, useEffect, useState, type ReactNode } from "react"
import { useForm } from "react-hook-form"
import {
  getGetBucketsQueryKey,
  getGetCompaniesCompanyIdQueryKey,
  getGetCompaniesQueryKey,
  useGetBuckets,
  useGetCompaniesCompanyId,
  useGetContacts,
  useGetContactsContactIdBuckets,
  useGetJobs,
  usePostBucketsContacts,
  usePutCompaniesCompanyId,
} from "openapi/api/endpoints"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { CompanyeSearchParams } from "./companies-tab"
import { INDUSTRIES } from "@/lib/industries"
import { CreateContactWizard } from "./create-contact-wizard"
import type { Contact } from "openapi/api/model"
import { PersonProfileSheet } from "./person-profile-sheet"
import { useUrlSyncedSearchState } from "@/hooks/use-url-synced-search-state"

interface CompanyDetailViewProps {
  companyId: string
  onBack: () => void
}

interface AddToBucketDialogProps {
  contact: Contact | null
  isOpen: boolean
  onClose: () => void
  onSelect: (id:string) => void
}

type CompanyEditFormValues = {
  name: string
  website: string
  industry: string
  size: string
  location: string
}

function getContactDisplayName(
  contact: Pick<Contact, "first_name" | "last_name"> | null | undefined,
  fallback = "Unnamed contact",
) {
  return [contact?.first_name, contact?.last_name].filter(Boolean).join(" ") || fallback
}

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("")
}

export function CompanyDetailView({ companyId, onBack }: CompanyDetailViewProps) {
  const {
    data,
    isLoading: isLoadingCompany,
    isError: hasCompanyError,
    refetch: refetchCompany,
  } = useGetCompaniesCompanyId(companyId)
  const {
    data: companyContacts,
    isLoading: isLoadingContacts,
    isError: hasContactsError,
    refetch: refetchContacts,
  } = useGetContacts({companyId})
  const {
    data: companyJobs,
    isLoading: isLoadingJobs,
    isError: hasJobsError,
    refetch: refetchJobs,
  } = useGetJobs({companyId})
  const { data:buckets } = useGetBuckets()
  const { mutateAsync: addToBucket } = usePostBucketsContacts()
  const { mutateAsync: updateCompany, isPending: isSavingCompany } = usePutCompaniesCompanyId()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const updateCompanySearch = useCallback(
    (options: { search: (prev: CompanyeSearchParams) => CompanyeSearchParams; replace?: boolean }) => {
      navigate({
        to: "/community/companies",
        search: options.search,
        replace: options.replace,
      })
    },
    [navigate],
  )
  const { patchSearch } = useUrlSyncedSearchState<CompanyeSearchParams>(updateCompanySearch)
  const [isEditingCompany, setIsEditingCompany] = useState(false)
  const companyForm = useForm<CompanyEditFormValues>({
    defaultValues: {
      name: "",
      website: "",
      industry: "",
      size: "",
      location: "",
    },
  })

  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [bucketDialogOpen, setBucketDialogOpen] = useState(false)
  const [contactForBucket, setContactForBucket] = useState<Contact | null>(null)
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null)

  const { companyTab, createContact } = useSearch({ strict: false }) as CompanyeSearchParams

  const activeTab = companyTab === "jobs" || companyTab === "contacts" ? companyTab : "jobs"
  const isCreateContactOpen = !!createContact
  const contacts = companyContacts ?? []
  const jobs = companyJobs ?? []
  const isLoadingCompanyDetail = isLoadingCompany || isLoadingContacts || isLoadingJobs
  const retryCompany = () => {
    void refetchCompany()
  }
  const retryContacts = () => {
    void refetchContacts()
  }
  const retryJobs = () => {
    void refetchJobs()
  }

  useEffect(() => {
    if (!data) return

    companyForm.reset({
      name: data.name ?? "",
      website: data.website ?? "",
      industry: data.industry ?? "",
      size: data.size ?? "",
      location: data.location ?? "",
    })
  }, [data, companyForm])

  const setTab = (next: "contacts" | "jobs") => {
    patchSearch({
      companyId,
      companyTab: next,
    })
  }

  const openCreateContact = () => {
    patchSearch({
      companyId,
      companyTab: "contacts",
      createContact: true,
    })
  }

  const closeCreateContact = () => {
    patchSearch({
      companyId,
      companyTab: "contacts",
      createContact: undefined,
    })
  }

  const handleSendEmail = (contact: Contact) => {
    setSelectedContact(contact)
    setEmailDialogOpen(true)
  }

  const handleAddToBucket = (contact: Contact) => {
    setContactForBucket(contact)
    setBucketDialogOpen(true)
  }

  const handleBucketSelection = async(bucketId: string) => {
    try{
      if (!bucketId || !contactForBucket) return

      const bucket = buckets?.find((b) => b.id === bucketId)
      if (!bucket) return

      await addToBucket({data: {bucket_id:bucketId, contact_ids:[contactForBucket.id]}})
      await queryClient.invalidateQueries({ queryKey: getGetBucketsQueryKey(), exact: false })
      await queryClient.refetchQueries({ queryKey: getGetBucketsQueryKey(), exact: false })
      toast.success("Added to bucket!",{
        description: `${contactForBucket?.first_name} has been added to ${bucket?.name}`,
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: '#a3e635'
        }
      })
    }catch(err){
      toast.error("An error occured!",{
        description: `${err}`,
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: "#dc2626",
        },
      });
    }

    setBucketDialogOpen(false)
    setContactForBucket(null)
  }

  const handleContactNameClick = (contact: Contact) => {
    setSelectedPersonId(contact.id)
  }

  const handleJobNameClick = (jobId: string) => {
    navigate({ to: `/jobs/${jobId}/overview` })
  }

  const handleSaveCompany = companyForm.handleSubmit(async (formData) => {
    try {
      await updateCompany({
        companyId,
        data: {
          name: formData.name.trim(),
          website: formData.website.trim() || null,
          industry: formData.industry.trim() || null,
          size: formData.size.trim() || null,
          location: formData.location.trim() || null,
        },
      })

      await queryClient.invalidateQueries({ queryKey: getGetCompaniesCompanyIdQueryKey(companyId) })
      await queryClient.invalidateQueries({ queryKey: getGetCompaniesQueryKey() })
      toast.success("Company saved successfully.")
      setIsEditingCompany(false)
    } catch {
      toast.error("Failed to save company. Please try again.")
    }
  })

  const handleCancelCompanyEdit = () => {
    companyForm.reset({
      name: data?.name ?? "",
      website: data?.website ?? "",
      industry: data?.industry ?? "",
      size: data?.size ?? "",
      location: data?.location ?? "",
    })
    setIsEditingCompany(false)
  }

  if (isLoadingCompanyDetail) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Loading company...
      </div>
    )
  }

  if (hasCompanyError) {
    return (
      <div className="py-8">
        <CompanyQueryErrorState
          message="Could not load this company."
          onRetry={retryCompany}
          action={
            <Button variant="outline" onClick={onBack}>
              Back to Companies
            </Button>
          }
        />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Company not found</p>
        <Button onClick={onBack} className="mt-4">
          Back to Companies
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center space-x-3">
          <Building2 className="h-8 w-8 text-brand-ink" />
          <div>
            <h2 className="text-2xl font-bold">{data?.name}</h2>
            {data?.industry && <Badge variant="secondary">{data?.industry}</Badge>}
          </div>
        </div>
      </div>

      <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-background via-background to-primary/5">
        <CardContent className="p-6">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">{data.name}</h3>
              <div className="flex flex-wrap items-center gap-2">
                {data.industry && <Badge variant="secondary">{data.industry}</Badge>}
                {data.size && <Badge variant="outline">{data.size} employees</Badge>}
              </div>
            </div>
            {!isEditingCompany && (
              <Button onClick={() => setIsEditingCompany(true)} size="sm" variant="outline">
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>

          {isEditingCompany && (
            <div className="mb-6 space-y-4 rounded-md border p-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="company-name">Company Name</Label>
                  <Input
                    id="company-name"
                    {...companyForm.register("name", { required: true })}
                    className="bg-white dark:bg-background text-black dark:text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-website">Website</Label>
                  <Input
                    id="company-website"
                    {...companyForm.register("website")}
                    placeholder="https://example.com"
                    className="bg-white dark:bg-background text-black dark:text-foreground"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="company-size">Company Size</Label>
                  <Input
                    id="company-size"
                    {...companyForm.register("size")}
                    placeholder="e.g., 50-200"
                    className="bg-white dark:bg-background text-black dark:text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-industry">Industry</Label>
                  <Select
                    value={companyForm.watch("industry") || "__none__"}
                    onValueChange={(value) => companyForm.setValue("industry", value === "__none__" ? "" : value)}
                  >
                    <SelectTrigger id="company-industry" className="bg-white dark:bg-background text-black dark:text-foreground">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No industry</SelectItem>
                      {INDUSTRIES.map((industry) => (
                        <SelectItem key={industry} value={industry}>
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="company-location">Location</Label>
                  <Input
                    id="company-location"
                    {...companyForm.register("location")}
                    placeholder="e.g., San Francisco, CA"
                    className="bg-white dark:bg-background text-black dark:text-foreground"
                  />
                </div>
                <div />
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveCompany} disabled={isSavingCompany || !companyForm.watch("name")?.trim()}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSavingCompany ? "Saving..." : "Save Changes"}
                </Button>
                <Button variant="outline" onClick={handleCancelCompanyEdit} disabled={isSavingCompany}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3 rounded-md border bg-background/70 p-4">
              {data?.description && <p className="text-sm text-muted-foreground">{data?.description}</p>}

              {data?.location && (
                <div className="flex items-center space-x-2 text-sm">
                  <MapPin className="h-4 w-4" />
                  <span>{data?.location}</span>
                </div>
              )}

              {data?.website && (
                <div className="flex items-center space-x-2 text-sm">
                  <Globe className="h-4 w-4" />
                  <a
                    href={data?.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-ink hover:underline"
                  >
                    {data?.website}
                  </a>
                </div>
              )}
            </div>

            <div className="space-y-3 rounded-md border bg-background/70 p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Jobs</div>
                  <div className="font-semibold">{hasJobsError ? "--" : jobs.length}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Contacts</div>
                  <div className="font-semibold">{hasContactsError ? "--" : contacts.length}</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground pt-1">
                <p>Created: {new Date(data?.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs  value={activeTab} onValueChange={(v) => setTab(v as "contacts" | "jobs")}>
        <TabsList>
          <TabsTrigger value="jobs">
            <Briefcase className="h-4 w-4 mr-2" />
            Jobs {hasJobsError ? null : `(${jobs.length})`}
          </TabsTrigger>
          <TabsTrigger value="contacts">
            <Users className="h-4 w-4 mr-2" />
            Contacts {hasContactsError ? null : `(${contacts.length})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contacts" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Company Contacts</h3>
              <p className="text-sm text-muted-foreground">Manage decision makers and client stakeholders</p>
            </div>
            <Button onClick={openCreateContact}>
              <Plus className="mr-2 h-4 w-4" />
              New Contact
            </Button>
          </div>

          {hasContactsError ? (
            <CompanyQueryErrorState
              message="Could not load contacts for this company."
              onRetry={retryContacts}
            />
          ) : contacts.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-sm text-muted-foreground">
                No contacts have been added for this company yet.
              </CardContent>
            </Card>
          ) : (
            contacts.map((contact) => {
              const contactName = getContactDisplayName(contact)

              return (
                <Card key={contact.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback>{getInitials(contactName)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p
                            className="font-medium cursor-pointer hover:text-brand-ink transition-colors"
                            onClick={() => handleContactNameClick(contact)}
                          >
                            {contactName}
                          </p>
                          <p className="text-sm text-muted-foreground">{contact.title}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <div className="flex items-center space-x-1">
                            <Mail className="h-3 w-3" />
                            <span>{contact.email}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Phone className="h-3 w-3" />
                            <span>{contact.phone}</span>
                          </div>
                          <Badge variant={contact.contact_type === "candidate" ? "default" : "secondary"}>{contact.contact_type}</Badge>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddToBucket(contact)}
                            className="h-8 w-8 p-0"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendEmail(contact)}
                            className="h-8 w-8 p-0"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })
          )}
        </TabsContent>

        <TabsContent value="jobs" className="space-y-4">
          {hasJobsError ? (
            <CompanyQueryErrorState
              message="Could not load jobs for this company."
              onRetry={retryJobs}
            />
          ) : jobs.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-sm text-muted-foreground">
                No jobs have been added for this company yet.
              </CardContent>
            </Card>
          ) : (
            jobs.map((job) => (
              <Card key={job.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className="font-medium cursor-pointer hover:text-brand-ink transition-colors"
                        onClick={()=>handleJobNameClick(job.id)}>{job.title}
                      </p>
                      <p className="text-sm text-muted-foreground">{job.department}</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Users className="h-3 w-3" />
                          <span>{job.applicants} candidates</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3" />
                          <span>{new Date(job.posted_at??'').toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Badge variant={job.status === "active" ? "default" : "secondary"}>{job.status}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      <EmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        contactId={selectedContact?.id ?? ""}
        recipientEmail={selectedContact?.email ?? ""}
        recipientName={selectedContact?.first_name ?? ""}
      />

      <AddToBucketDialog
      contact={contactForBucket}
      isOpen={bucketDialogOpen}
      onClose={()=>setBucketDialogOpen(false)}
      onSelect={handleBucketSelection}/>

      <DialogShell
        open={isCreateContactOpen}
        onOpenChange={(open) => (open ? openCreateContact() : closeCreateContact())}
        wide
        title="Create New Contact"
      >
        {/* The wizard owns its own steps and actions. */}
        <CreateContactWizard
          initialCompanyId={companyId}
          initialCompanyName={data?.name ?? ""}
          onComplete={closeCreateContact}
          onCancel={closeCreateContact}
        />
      </DialogShell>

      <PersonProfileSheet
        personId={selectedPersonId}
        isOpen={!!selectedPersonId}
        onClose={() => setSelectedPersonId(null)}
      />
    </div>
  )
}

function CompanyQueryErrorState({
  message,
  onRetry,
  action,
}: {
  message: string
  onRetry: () => void
  action?: ReactNode
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-6 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
        <span>{message}</span>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onRetry}>
            Retry
          </Button>
          {action}
        </div>
      </CardContent>
    </Card>
  )
}

function AddToBucketDialog({contact, isOpen, onClose, onSelect}:AddToBucketDialogProps){

  const { data:buckets } = useGetBuckets()
  const { data:contactBucketContacts } = useGetContactsContactIdBuckets(contact?.id ?? "", {query:{enabled: !!contact?.id}})

  const contactBuckets = contactBucketContacts?.map((bucketContact) => bucketContact.buckets) ?? []

  return (
    <DialogShell
      open={isOpen}
      onOpenChange={onClose}
      title={`Add ${getContactDisplayName(contact, "this contact")} to Bucket`}
      description="A bucket is a set of people you want to stay in front of, on a rhythm."
    >
          <FieldShell label="Bucket">
            <Select onValueChange={onSelect}>
              <SelectTrigger
                aria-label="Bucket"
                className={cn(FIELD_UNDERLINE, "h-auto pb-[7px] text-[12.5px] leading-[1.4]")}
              >
                <SelectValue placeholder="Select a bucket" />
              </SelectTrigger>
              <SelectContent>
                {buckets?.filter((bucket) => !contactBuckets.some((contactBucket) => contactBucket.id === bucket.id)).map((bucket) => (
                  <SelectItem key={bucket.id} value={bucket.id??''}>
                    <div className="flex flex-col">
                      <span className="font-medium">{bucket.name}</span>
                      {bucket.description && (
                        <span className="text-xs text-muted-foreground">{bucket.description}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldShell>
    </DialogShell>
  )
}
